import { create } from 'zustand';
import { subscribeWithSelector, persist } from '../../../node_modules/zustand/esm/middleware.js';
import { useQuerySubscription } from '../queries/useQuerySubscription.js';
import dayjs from 'dayjs';
import { useRef, useEffect } from 'react';
import { appRoutes } from '../../app/routing/routes.config.js';
import { formatPercentage } from '@fabric-app/utils/number';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { track } from '../../analytics/hooks/useAnalytics.js';
import { captureMessage } from '@sentry/core';

const STORE_NAME = "subscription-limit-banner";
const DISMISS_THRESHOLD_DAYS = 2;
const useSubscriptionBannerStore = create()(subscribeWithSelector(persist((set) => ({
  dismissedAt: null,
  dismiss: () => {
    set({
      dismissedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
    track(AnalyticsEvents.SubscriptionLimitBannerDismissed);
  },
  banner: null,
  setBanner: (banner, subscription) => {
    captureMessage("Should invoke limit banner", {
      extra: {
        banner,
        subscription
      }
    });
    track(AnalyticsEvents.SubscriptionLimitBannerShown, {
      banner,
      subscription
    });
    set({
      banner,
      dismissedAt: null
    });
  }
}), {
  name: STORE_NAME
})));
const createBanner = (title, options) => {
  return {
    id: "subscription-limit",
    type: "banner",
    title,
    content: null,
    severity: options?.severity ?? "warning",
    action: {
      label: options?.actionTitle ?? "Upgrade",
      type: "url",
      url: appRoutes.settings.account,
      target: "_self",
      onClick: () => {
        track(AnalyticsEvents.SubscriptionLimitBannerClicked);
      }
    },
    dismissable: true
  };
};
const getUsedCreditsPct = (data) => {
  return Math.floor(data.used / data.limit * 100) / 100;
};
const useSubscriptionLimitBanner = (args) => {
  const disabled = args?.disabled ?? false;
  const { data: subscription, error } = useQuerySubscription({
    enabled: !disabled
  });
  const { banner: activeBanner, dismiss, setBanner, dismissedAt } = useSubscriptionBannerStore();
  const activeBannerRef = useRef(activeBanner);
  activeBannerRef.current = activeBanner;
  useEffect(() => {
    if (!subscription || !!error || disabled) {
      return;
    }
    const usageStoragePct = subscription.usage.storage ? Math.floor(subscription.usage.storage.used / subscription.usage.storage.limit * 100) / 100 : 0;
    const usageCreditsPct = subscription.usage.credits ? getUsedCreditsPct(subscription.usage.credits) : 0;
    if (usageStoragePct >= 1 || usageCreditsPct >= 1) {
      if (dayjs(dismissedAt).isAfter(dayjs().subtract(DISMISS_THRESHOLD_DAYS, "day")) || /**
      * previous orange, override
      */
      activeBannerRef.current?.severity === "urgent") {
        return;
      }
      if (usageStoragePct >= 1) {
        setBanner(createBanner("Your storage is full! While full, your Fabric is read-only"), subscription);
        return;
      }
      if (usageCreditsPct >= 1) {
        setBanner(createBanner("You’re out of thinking credits! You can’t save new items or use AI features until you have enough credits", {
          severity: "urgent",
          actionTitle: "Top up credits"
        }), subscription);
        return;
      }
      return;
    }
    if (activeBannerRef.current || dayjs(dismissedAt).isAfter(dayjs().subtract(DISMISS_THRESHOLD_DAYS, "day"))) {
      return;
    }
    if (usageStoragePct > 0.9) {
      setBanner(createBanner(`You’ve used ${formatPercentage(usageStoragePct)} of your storage! If you run out of storage, your Fabric will become read-only`), subscription);
      return;
    }
    if (usageCreditsPct > 0.9) {
      setBanner(createBanner(`You’ve used ${formatPercentage(usageCreditsPct)} of your thinking credits! If you run out of credits, you won’t be able to save new items or use AI features`), subscription);
      return;
    }
  }, [
    subscription,
    error,
    dismissedAt,
    dismiss,
    setBanner,
    disabled
  ]);
  return {
    banner: dismissedAt ? null : activeBanner,
    dismiss
  };
};

export { useSubscriptionLimitBanner };
//# sourceMappingURL=useSubscriptionLimitBanner.js.map
